<script setup lang="ts">
import { TABBAR_SECTIONS } from '#shared/area-riservata-nav';
import { useAreaRiservataPaths } from '~/composables/useAreaRiservataPaths';

const route = useRoute();
const { loadPaths } = useAreaRiservataPaths();

onMounted(() => {
	loadPaths();
});
const config = useRuntimeConfig();
const { data: siteData } = await useFetch<{ globals?: { favicon?: string | null; logo?: string | null } }>('/api/site-data', { key: 'site-data' });

const sidebarLogoUrl = computed(() => {
	const g = siteData.value?.globals;
	const id = g?.favicon || g?.logo;
	if (!id || !config.public.directusUrl) return '/images/logo.svg';
	return `${config.public.directusUrl}/assets/${id}`;
});

useHead({
	link: [
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200',
		},
	],
	style: [
		{
			innerHTML: `.material-symbols-outlined { font-family: 'Material Symbols Outlined'; font-weight: normal; font-style: normal; font-size: 24px; line-height: 1; letter-spacing: normal; text-rendering: optimizeLegibility; -webkit-font-smoothing: antialiased; }`,
		},
		{
			innerHTML: `.area-riservata-tab-link:hover { color: #A21B1F !important; } .area-riservata-tab-link:hover .material-symbols-outlined { color: #A21B1F !important; }`,
		},
	],
});

const currentPath = computed(() => route.path.replace(/\/$/, ''));
/** Pagine senza sottosezioni (solo contenuto principale): nascondi sidebar. */
const isDashboardIndex = computed(() =>
	currentPath.value === '/area-riservata' ||
	currentPath.value === '/area-riservata/appuntamenti' ||
	currentPath.value === '/area-riservata/profilo' ||
	currentPath.value.startsWith('/area-riservata/profilo/')
);

const isActive = (path: string) => {
	const current = currentPath.value;
	if (path === '/area-riservata') {
		return current === '/area-riservata';
	}
	return current === path || (path.length > 0 && current.startsWith(path + '/'));
};
</script>

<template>
	<div class="h-screen overflow-hidden bg-gray-50 flex flex-col pb-20 lg:pb-0">
		<div class="flex flex-1 min-h-0 overflow-hidden">
			<!-- Sidebar verticale desktop: fissa a sinistra, non scorre -->
			<nav
				class="hidden lg:flex lg:flex-col w-24 shrink-0 border-r border-gray-200 bg-black text-white pt-4 pb-4 items-center overflow-y-auto"
				role="tablist"
				aria-label="Navigazione area riservata"
			>
				<NuxtLink
					to="/area-riservata"
					class="mb-4 flex shrink-0 items-center justify-center w-10 h-10 rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
					aria-label="Dashboard"
				>
					<img :src="sidebarLogoUrl" alt="" class="w-full h-full object-contain" />
				</NuxtLink>
				<NuxtLink
					v-for="tab in TABBAR_SECTIONS"
					:key="tab.path"
					:to="tab.path"
					:class="[
						'area-riservata-tab-link flex flex-col items-center justify-center gap-0.5 py-2.5 w-full text-xs transition-colors',
						isActive(tab.path)
							? 'text-[#A21B1F] font-medium'
							: 'text-white hover:bg-white/5 rounded-lg',
					]"
					:aria-current="isActive(tab.path) ? 'page' : undefined"
				>
					<span class="material-symbols-outlined text-2xl" :class="{ 'fill-current': isActive(tab.path) }">
						{{ tab.icon }}
					</span>
					<span class="text-center leading-tight">{{ tab.label }}</span>
				</NuxtLink>
			</nav>
			<!-- Sidebar secondario disabilitato per rimuovere il sottomenu laterale -->
			<main class="flex-1 min-h-0 overflow-auto min-w-0">
				<NuxtPage />
			</main>
		</div>
		<!-- Tabbar in basso solo su mobile -->
		<nav
			class="lg:hidden fixed bottom-0 left-0 right-0 bg-black pb-safe z-50"
			role="tablist"
		>
			<div class="flex justify-around items-center h-16 max-w-lg mx-auto">
				<NuxtLink
					v-for="tab in TABBAR_SECTIONS"
					:key="tab.path"
					:to="tab.path"
					:class="[
						'area-riservata-tab-link flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-xs transition-colors',
						isActive(tab.path)
							? 'text-[#A21B1F] font-medium'
							: 'text-white',
					]"
					:aria-current="isActive(tab.path) ? 'page' : undefined"
				>
					<span class="material-symbols-outlined text-2xl" :class="{ 'fill-current': isActive(tab.path) }">
						{{ tab.icon }}
					</span>
					<span>{{ tab.label }}</span>
				</NuxtLink>
			</div>
		</nav>
	</div>
</template>
